import {
  CompleteHocuspocusProviderWebsocketConfiguration,
  HocuspocusProvider,
} from '@hocuspocus/provider';
import * as Y from 'yjs';

import { getCollaborationServiceUrl } from 'src/utils/collaboration/config';

/**
 * Callback invoked when the Y.Doc receives a remote update.
 */
export type OnRemoteUpdate = (doc: Y.Doc) => void;

/**
 * Callback invoked when the provider's connection status changes.
 */
export type OnStatusChange = (status: string) => void;

/**
 * Options for creating a collaboration connection.
 */
export interface CollaborationConnectOptions {
  /** The project ID (used as the document name) */
  projectId: string;
  /** Called when the Y.Doc is updated remotely */
  onRemoteUpdate: OnRemoteUpdate;
  /** Called when the connection status changes (optional) */
  onStatusChange?: OnStatusChange;
  /** Called when the provider disconnects (optional) */
  onDisconnect?: () => void;
  /** Override the WebSocket URL (for testing) */
  url?: string;
}

/**
 * Active connection state for a single project.
 */
interface ActiveConnection {
  provider: HocuspocusProvider;
  doc: Y.Doc;
  status: string;
  synced: boolean;
  // `true` until the server's `Authenticated` message confirms editor role.
  // Defaulting to read-only avoids flashing editable UI for viewers.
  readOnly: boolean;
  // Project-wide undo/redo (VET-6292). Scoped to the root 'project' map and
  // tracking only `'local'`-origin writes, so each writer undoes only their
  // own edits.
  undoManager: Y.UndoManager;
  updateHandler: (update: Uint8Array, origin: unknown) => void;
}

/** Map of active connections keyed by project ID. */
const activeConnections = new Map<string, ActiveConnection>();

/** Listeners notified whenever a project's connection status changes. */
type StatusListener = (projectId: string, status: string) => void;
const statusListeners = new Set<StatusListener>();

/** Listeners notified whenever a project's read-only flag changes. */
type ReadOnlyListener = (projectId: string, readOnly: boolean) => void;
const readOnlyListeners = new Set<ReadOnlyListener>();

/** Listeners notified whenever a project's WebSocket authentication fails. */
type AuthFailureListener = (projectId: string, reason: string) => void;
const authFailureListeners = new Set<AuthFailureListener>();

/**
 * Subscribe to connection status changes across all projects.
 *
 * @param listener - Called with (projectId, status) on every status change
 * @returns An unsubscribe function
 */
export const addStatusChangeEventListener = (
  listener: StatusListener
): (() => void) => {
  statusListeners.add(listener);
  return () => {
    statusListeners.delete(listener);
  };
};

/**
 * Subscribe to read-only role changes across all projects. Fires once per
 * project when the server's `Authenticated` message arrives with the user's
 * effective role.
 *
 * @param listener - Called with (projectId, readOnly) on every change
 * @returns An unsubscribe function
 */
export const addReadOnlyChangeEventListener = (
  listener: ReadOnlyListener
): (() => void) => {
  readOnlyListeners.add(listener);
  return () => {
    readOnlyListeners.delete(listener);
  };
};

/**
 * Subscribe to WebSocket auth failures (401/403 from SCS). Fires when the
 * user is not logged in or lacks access to the project.
 *
 * @param listener - Called with (projectId, reason)
 * @returns An unsubscribe function
 */
export const addAuthFailureEventListener = (
  listener: AuthFailureListener
): (() => void) => {
  authFailureListeners.add(listener);
  return () => {
    authFailureListeners.delete(listener);
  };
};

const notifyStatusListeners = (projectId: string, status: string) => {
  statusListeners.forEach((listener) => listener(projectId, status));
};

const notifyReadOnlyListeners = (projectId: string, readOnly: boolean) => {
  readOnlyListeners.forEach((listener) => listener(projectId, readOnly));
};

const notifyAuthFailureListeners = (projectId: string, reason: string) => {
  authFailureListeners.forEach((listener) => listener(projectId, reason));
};

/**
 * Connect to the Collaboration Service for a given project.
 *
 * Creates a new Y.Doc, establishes a HocuspocusProvider WebSocket connection,
 * and wires up remote update observation. Authentication uses the browser's
 * Django `sessionid` cookie, which nginx forwards on the WebSocket upgrade
 * — there is no client-side token.
 *
 * @param options - Connection options
 * @returns The Y.Doc for the connection (for writing local changes)
 */
export const connect = (options: CollaborationConnectOptions): Y.Doc => {
  const {
    projectId,
    onRemoteUpdate,
    onStatusChange,
    onDisconnect,
    url = getCollaborationServiceUrl(),
  } = options;

  // If already connected, disconnect first
  if (activeConnections.has(projectId)) {
    disconnect(projectId);
  }

  const doc = new Y.Doc();

  // Project-wide undo/redo backed by the same Y.Doc that drives collaboration
  // (VET-6292). Scoped to the root 'project' map, tracking only `'local'`
  // origin — the origin the middleware tags this client's edits with — so
  // remote merges (origin === provider) and the initial full-project
  // population (origin 'init') are never undoable. `captureTimeout: 0` keeps
  // one undo entry per GoJS-batched user action; GoJS already groups a gesture
  // into a single transaction, so no additional time-based merging is wanted.
  const undoManager = new Y.UndoManager(doc.getMap('project'), {
    trackedOrigins: new Set(['local']),
    captureTimeout: 0,
  });

  // WebSocket reconnection options — forwarded to HocuspocusProviderWebsocket
  // when no explicit websocketProvider is given.
  //
  // Strategy: retry indefinitely (maxAttempts: 0) with exponential backoff
  // starting at 1 s, doubling each attempt, capped at 30 s, with jitter to
  // avoid thundering-herd reconnects after a service restart.
  //
  // Note: `timeout` (a whole-sequence timeout from @lifeomic/attempt) is
  // intentionally omitted — the default of 0 means no global timeout, so
  // the retry loop runs until the connection succeeds or is explicitly
  // destroyed (e.g. on auth failure or page unload).
  const reconnectOptions: Partial<CompleteHocuspocusProviderWebsocketConfiguration> =
    {
      maxAttempts: 0,
      delay: 1000,
      factor: 2,
      maxDelay: 30000,
      jitter: true,
    };

  const provider = new HocuspocusProvider({
    ...reconnectOptions,
    url,
    // Keep the trailing slash on `/collaboration/` — nginx 301-redirects the
    // non-slash form, and WebSocket upgrades can't follow HTTP redirects.
    preserveTrailingSlash: true,
    name: projectId,
    document: doc,
    onSynced: ({ state }) => {
      if (state) {
        const conn = activeConnections.get(projectId);
        if (conn) {
          conn.synced = true;
        }
        // Drop any pre-sync entries so a previously-collaborated project's
        // seeded state (applied during sync) is never undoable.
        undoManager.clear();
        // Apply the server's synced state to Redux. The SCS-persisted Y.Doc is
        // the source of truth and can be ahead of the Cloud API snapshot the
        // client loaded on mount (collaborative edits not yet flushed back to
        // cloud storage). An empty 'project' map means a fresh room never
        // collaborated on — keep the cloud snapshot and let the first local
        // edit seed the doc. Edits are blocked during the read-only connect
        // window, so this can't clobber unsaved local changes.
        if (doc.getMap('project').size > 0) {
          onRemoteUpdate(doc);
        }
      }
    },
    onStatus: ({ status }) => {
      const conn = activeConnections.get(projectId);
      if (conn) {
        conn.status = status;
      }
      notifyStatusListeners(projectId, status);
      if (onStatusChange) {
        onStatusChange(status);
      }
    },
    onAuthenticated: ({ scope }) => {
      // Hocuspocus maps the server's `readOnly` flag on connectionConfig to
      // this scope. SCS sets `readOnly: true` for the viewer role.
      const readOnly = scope === 'readonly';
      const conn = activeConnections.get(projectId);
      if (conn && conn.readOnly !== readOnly) {
        conn.readOnly = readOnly;
      }
      notifyReadOnlyListeners(projectId, readOnly);
    },
    onAuthenticationFailed: ({ reason }) => {
      notifyAuthFailureListeners(projectId, reason);
      // Stop the reconnect loop — the user doesn't have access, retrying
      // won't help. The legacy save path takes over once we disconnect.
      disconnect(projectId);
    },
    onDisconnect: onDisconnect ?? (() => null),
  });

  // Listen for remote updates (updates from other peers via the server).
  // Per-event updates that arrive *during* the initial sync are ignored here:
  // applying each incremental update as it streams in would thrash Redux. The
  // fully-synced state is instead applied once in `onSynced` above (when the
  // doc is non-empty), so the client still picks up any collaborative edits the
  // SCS hasn't yet flushed back to the Cloud API.
  const updateHandler = (_update: Uint8Array, origin: unknown) => {
    const conn = activeConnections.get(projectId);
    const isRemote = origin === provider;

    if (!isRemote) {
      return;
    }

    if (!conn?.synced) {
      return;
    }

    onRemoteUpdate(doc);
  };
  doc.on('update', updateHandler);

  activeConnections.set(projectId, {
    provider,
    doc,
    status: 'connecting',
    synced: false,
    readOnly: true,
    undoManager,
    updateHandler,
  });

  return doc;
};

/**
 * Disconnect from the Collaboration Service for a given project.
 *
 * Destroys the HocuspocusProvider and cleans up the Y.Doc.
 *
 * @param projectId - The project to disconnect from
 */
export const disconnect = (projectId: string): void => {
  const connection = activeConnections.get(projectId);
  if (!connection) {
    return;
  }

  connection.doc.off('update', connection.updateHandler);
  connection.undoManager.destroy();
  connection.provider.destroy();
  connection.doc.destroy();
  activeConnections.delete(projectId);
  notifyStatusListeners(projectId, 'disconnected');
};

/**
 * Disconnect all active collaboration connections.
 * Called on window beforeunload or when the user logs out.
 */
export const disconnectAll = (): void => {
  const projectIds = Array.from(activeConnections.keys());
  projectIds.forEach((projectId) => disconnect(projectId));
};

/**
 * Get the Y.Doc for an active collaboration connection.
 *
 * @param projectId - The project to get the doc for
 * @returns The Y.Doc, or undefined if not connected
 */
export const getDoc = (projectId: string): Y.Doc | undefined => {
  return activeConnections.get(projectId)?.doc;
};

/**
 * Get the Y.UndoManager for an active collaboration connection.
 *
 * Drives project-wide undo/redo (VET-6292). Tracks only this client's
 * `'local'`-origin edits.
 *
 * @param projectId - The project to get the undo manager for
 * @returns The Y.UndoManager, or undefined if not connected
 */
export const getUndoManager = (
  projectId: string
): Y.UndoManager | undefined => {
  return activeConnections.get(projectId)?.undoManager;
};

/**
 * Check whether a collaboration connection is active for a given project.
 * Returns true only when the WebSocket is fully connected, not during
 * the connecting or reconnecting phase.
 *
 * @param projectId - The project to check
 * @returns true if connected
 */
export const isConnected = (projectId: string): boolean => {
  const connection = activeConnections.get(projectId);
  return connection?.status === 'connected';
};

/**
 * Get the current connection status for a given project.
 *
 * @param projectId - The project to check
 * @returns The status string ('connecting', 'connected', 'disconnected'),
 *          or undefined if no connection exists
 */
export const getStatus = (projectId: string): string | undefined => {
  return activeConnections.get(projectId)?.status;
};

/**
 * Get the current read-only flag for a project's collaboration connection.
 *
 * @param projectId - The project to check
 * @returns true if the user has viewer role, false for editor, or undefined
 *          if there is no connection for the project
 */
export const isReadOnly = (projectId: string): boolean | undefined => {
  return activeConnections.get(projectId)?.readOnly;
};
